import { Component, computed, effect, ElementRef, inject, input, signal, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { CardViewService, DashboardsService, HelpersService, IFirmUsersService } from "@app/shared";
import { CardDisplayService } from "@app/shared/card-display/card-display.service";
import { JobsAndBillingJobTypesService } from "@app/shared/jobs-and-billing-job-types-service/jobs-and-billing-job-types.service";
import { JobsBillingCard } from "@app/views-interfaces";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { UsersService } from "@shared/users-service/users.service";
import { format, fromUnixTime } from "date-fns";
import { UserDataModel } from "src/app/settings-interfaces/users.models";

@Component({
    selector: "wk-card-jobs-billing",
    templateUrl: "./card-jobs-billing.component.html",
    styleUrls: ["../card-t1-component/card-t1.component.scss", "./card-jobs-billing.component.scss"],
    standalone: false,
    host: {
        class: "wk-card-t1 wk-card-job",
        "[id]": "divId()",
        "(click)": "openCard($event)",
        "(mouseenter)": "onMouseEnter()",
        "(mouseleave)": "onMouseLeave()",
        "[class.wk-card-t1__column]": "format() === 'column'",
        "[class.wk-card-t1__grid]": "format() === 'grid'",
        "[class.row]": "format() === 'grid'",
        "[class.wk-card-t1__column--quiet]": "format() === 'column' && quietDaysFromNow > 0",
        "[class.wk-card-t1__column--danger]": "this.cardViewService.currentViewType !== 'completed' && format() === 'column' && (isStepAlert)",
        "[class.wk-card-t1__column--warning]":
            "this.cardViewService.currentViewType !== 'completed' && format() === 'column' && (isStepWarning && !isStepAlert)",
        "[class.notStarted]": "format() === 'grid' && cardViewService.currentViewType === 'notStarted'",
        "[class.completed]": "format() === 'grid' && cardViewService.currentViewType === 'completed'",
    },
})
export class CardJobsBillingComponent {
    private readonly router = inject(Router);
    private readonly usersService = inject(UsersService);
    private readonly jobsAndBillingJobTypesService = inject(JobsAndBillingJobTypesService);
    private readonly helpersService = inject(HelpersService);
    private readonly translateService = inject(TranslateService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly dashboardsService = inject(DashboardsService);
    public readonly cardViewService = inject(CardViewService);
    private readonly iFirmUsersService = inject(IFirmUsersService);

    tooltipDisabled = signal(true);
    isHovered = signal(false);

    protected tooltipText = computed(() => () => this.column_body_date_tooltip());
    private readonly cardJobColumnName = viewChild<ElementRef<HTMLElement>>("cardJobColumnName");
    protected assignedUser: UserDataModel | null = null;

    isDeadlineWarning = false;
    isDeadlineAlert = false;
    isStepWarning = false;
    isStepAlert = false;
    isTodayDeadlineDate = false;
    deadlineDate = null;
    deadlineDaysFromNow = null;
    deadlineDaysFromNow_Absolute = null;
    daysSinceInStep = 0;
    daysSinceReady = 0;
    quietDaysFromNow = 0;
    currentLanguage = "en-CA";

    deadlineTip_Default = false;
    deadlineTip_Warning = false;
    deadlineTip_Alert = false;
    deadlineTip_Critical = false;

    format = input("column");
    statusIds = input<string[]>();
    dragDisabled = input(false);
    cardData = input<JobsBillingCard>();

    protected readonly divId = computed(() => {
        const cardData = this.cardData();

        if (!cardData?.cardId) return "";

        return "div_" + cardData.cardId.replaceAll(/[{}]/g, "");
    });

    protected jobTypeShortCode = computed(() => {
        const cardData = this.cardData();

        if (!cardData?.job) return null;

        return this.jobsAndBillingJobTypesService.jobTypes().get(cardData.job?.typeId)?.shortCode ?? null;
    });

    constructor() {
        this.cardDisplayService.calcCardFieldLabelsEvent$.pipe(takeUntilDestroyed()).subscribe((data) => {
            if (this.cardData()?.cardId === data.cardId) {
                this.calcAllFieldLabels();
            }
        });

        effect(() => {
            const data = this.cardData();
            const statusIds = this.statusIds();

            if (!data || !statusIds) return;

            this.getAssignedUser();
            this.calcAllFieldLabels();
        });

        this.helpersService.langChangeEvent$.pipe(takeUntilDestroyed()).subscribe((lang) => {
            this.calcAllFieldLabels(true);
        });

        this.cardViewService.refreshCardsViewLabelsEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.calcAllFieldLabels();
        });

        this.translateService.onLangChange.pipe(takeUntilDestroyed()).subscribe((event: LangChangeEvent) => {
            this.currentLanguage = this.helpersService.getLang();
        });

        effect(() => {
            const cardJobColumnName = this.cardJobColumnName();
            if (!cardJobColumnName) return;
            this.tooltipDisabled.set(cardJobColumnName?.nativeElement.scrollWidth <= cardJobColumnName?.nativeElement.clientWidth);
        });
    }

    ngOnInit() {
        this.currentLanguage = this.helpersService.getLang();
    }

    calcAllFieldLabels(skipCache = false) {
        const result = this.cardDisplayService.calcCardFieldLabels(this.cardData(), this.statusIds(), skipCache);

        this.deadlineDaysFromNow = result.deadlineDaysFromNow;
        this.deadlineDate = result.deadlineDate;
        this.daysSinceInStep = result.daysSinceInStep;
        this.daysSinceReady = result.daysSinceReady;
        this.isStepWarning = result.isStepWarning;
        this.isStepAlert = result.isStepAlert;
        this.isDeadlineWarning = result.isDeadlineWarning;
        this.isDeadlineAlert = result.isDeadlineAlert;
        this.quietDaysFromNow = result.quietDaysFromNow;

        this.setDeadlineTooltip();
        this.deadlineDaysFromNow_Absolute = Math.abs(this.deadlineDaysFromNow);
        this.isTodayDeadlineDate = this.isDeadlineDateToday(this.cardData(), this.deadlineDaysFromNow);
    }

    protected readonly cardTitle = computed(() => {
        const contactInformation = this.cardData()?.contactInformation;
        const name = [contactInformation?.firstName, contactInformation?.lastName].filter(Boolean).join(" ");
        return name || this.cardData()?.name || "";
    });

    protected openCard(e: MouseEvent): void {
        e.preventDefault();
        this.router.navigate(["/" + this.router.url, this.cardData()?.cardId]);
    }

    onMouseEnter(): void {
        this.isHovered.set(true);
    }
    onMouseLeave(): void {
        this.isHovered.set(false);
    }

    private async getAssignedUser() {
        this.assignedUser = null;
        const users = (await this.iFirmUsersService.getUsers()).filter((it) => it.Active && it.LicenseEnabled);

        for (const user of users) {
            if (user.UserId.toString() === this.cardData()?.job?.currentlyResponsible) {
                this.assignedUser = {
                    FirstName: user.FirstName,
                    LastName: user.LastName,
                } as UserDataModel;
                break;
            }
        }
    }

    private column_body_date_tooltip(): string {
        let tooltip = "";
        if (!this.isDeadlineWarning && !this.isDeadlineAlert) {
            tooltip = this.translateService.instant("JobsAndBilling.TargetEndDate");
        }

        if (this.deadlineTip_Warning && !this.deadlineTip_Critical && !this.deadlineTip_Alert) {
            tooltip =
                this.deadlineDaysFromNow_Absolute === 0 || this.deadlineDaysFromNow_Absolute === 1
                    ? this.translateService.instant("JobsAndBilling.Tooltips.DeadlineWarning1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("JobsAndBilling.Tooltips.AverageDay") +
                      this.translateService.instant("JobsAndBilling.Tooltips.DeadlineWarning2")
                    : this.translateService.instant("JobsAndBilling.Tooltips.DeadlineWarning1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("JobsAndBilling.Tooltips.AverageDays") +
                      this.translateService.instant("JobsAndBilling.Tooltips.DeadlineWarning2");
        }
        if (this.deadlineTip_Alert && !this.deadlineTip_Critical && !this.deadlineTip_Warning && this.isTodayDeadlineDate) {
            tooltip =
                this.deadlineDaysFromNow_Absolute === 0 || this.deadlineDaysFromNow_Absolute === 1
                    ? this.translateService.instant("JobsAndBilling.Tooltips.DeadlineAlert1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("JobsAndBilling.Tooltips.AverageDay") +
                      " " +
                      this.translateService.instant("JobsAndBilling.Tooltips.TimeDuration2") +
                      this.translateService.instant("JobsAndBilling.Tooltips.DeadlineAlert2")
                    : this.translateService.instant("JobsAndBilling.Tooltips.DeadlineAlert1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("JobsAndBilling.Tooltips.AverageDays") +
                      " " +
                      this.translateService.instant("JobsAndBilling.Tooltips.TimeDuration2") +
                      this.translateService.instant("JobsAndBilling.Tooltips.DeadlineAlert2");
        }
        if (this.deadlineTip_Alert && !this.deadlineTip_Critical && !this.deadlineTip_Warning && !this.isTodayDeadlineDate) {
            tooltip =
                this.deadlineDaysFromNow_Absolute === 0 || this.deadlineDaysFromNow_Absolute === 1
                    ? this.translateService.instant("JobsAndBilling.Tooltips.DeadlineAlert1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("JobsAndBilling.Tooltips.AverageDay") +
                      " " +
                      this.translateService.instant("JobsAndBilling.Tooltips.TimeDuration1") +
                      this.translateService.instant("JobsAndBilling.Tooltips.DeadlineAlert2")
                    : this.translateService.instant("JobsAndBilling.Tooltips.DeadlineAlert1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("JobsAndBilling.Tooltips.AverageDays") +
                      " " +
                      this.translateService.instant("JobsAndBilling.Tooltips.TimeDuration1") +
                      this.translateService.instant("JobsAndBilling.Tooltips.DeadlineAlert2");
        }
        if (this.deadlineTip_Critical && !this.deadlineTip_Warning && !this.deadlineTip_Alert) {
            tooltip =
                this.deadlineDaysFromNow_Absolute === 0 || this.deadlineDaysFromNow_Absolute === 1
                    ? this.translateService.instant("JobsAndBilling.Tooltips.DeadlineCritical1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("JobsAndBilling.Tooltips.AverageDay") +
                      this.translateService.instant("JobsAndBilling.Tooltips.DeadlineCritical2")
                    : this.translateService.instant("JobsAndBilling.Tooltips.DeadlineCritical1") +
                      " " +
                      this.deadlineDaysFromNow_Absolute +
                      this.translateService.instant("JobsAndBilling.Tooltips.AverageDays") +
                      this.translateService.instant("JobsAndBilling.Tooltips.DeadlineCritical2");
        }
        return tooltip;
    }

    setDeadlineTooltip(): boolean {
        this.deadlineTip_Default = false;
        this.deadlineTip_Warning = false;
        this.deadlineTip_Alert = false;
        this.deadlineTip_Critical = false;
        if (this.dashboardsService.currentDashboard().DaysBeforeDeadlineWarning != null) {
            if (this.deadlineDate != null) {
                if (this.deadlineDaysFromNow) {
                    if (
                        this.deadlineDaysFromNow < 0 &&
                        Math.abs(this.deadlineDaysFromNow) > this.dashboardsService.currentDashboard().DaysBeforeDeadlineWarning
                    ) {
                        this.deadlineTip_Default = true;
                        return true;
                    } else if (
                        this.deadlineDaysFromNow < 0 &&
                        this.dashboardsService.currentDashboard().DaysBeforeDeadlineWarning >= Math.abs(this.deadlineDaysFromNow) &&
                        Math.abs(this.deadlineDaysFromNow) > this.dashboardsService.currentDashboard().DaysBeforeDeadlineAlert
                    ) {
                        this.deadlineTip_Warning = true;
                        return true;
                    } else if (
                        this.deadlineDaysFromNow < 0 &&
                        this.dashboardsService.currentDashboard().DaysBeforeDeadlineAlert >= Math.abs(this.deadlineDaysFromNow)
                    ) {
                        this.deadlineTip_Alert = true;
                        return true;
                    } else if (this.deadlineDaysFromNow > 0) {
                        this.deadlineTip_Critical = true;
                        return true;
                    }
                } else if (this.deadlineDaysFromNow == 0 && this.deadlineDate != null) {
                    this.deadlineTip_Alert = true;
                    return true;
                }
            } else {
                this.deadlineTip_Default = true;
                return true;
            }
        } else {
            this.deadlineTip_Default = true;
            return true;
        }
    }

    isDeadlineDateToday(cardData: any, dealineDays) {
        if (dealineDays < 0) {
            return true;
        } else {
            if (cardData.job?.targetEndDate) {
                return format(fromUnixTime(cardData.job.targetEndDate), "MMMM d, yyyy") === format(Date.now(), "MMMM d, yyyy");
            } else if (cardData.deadLine) {
                return format(fromUnixTime(cardData.deadLine), "MMMM d, yyyy") === format(Date.now(), "MMMM d, yyyy");
            } else {
                return false;
            }
        }
    }
}
